import '~/assets/styles/index.scss';

import React from 'react';
import { GeoLocationProvider } from '~/app/context';

const ATTR_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'cq_cmp',
  'cq_con',
  'cq_med',
  'cq_net',
  'cq_plt',
  'cq_src',
  'cq_term',
  'city',
];

const GTM_CODE = process.env.GTM_CODE;
const TOUCH_STORAGE = { first: 'traffic_first_touch', last: 'traffic_last_touch' };
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

let attributionInitialized = false;
let gtmInitialized = false;

export const wrapRootElement = ({ element }) => <GeoLocationProvider>{element}</GeoLocationProvider>;

const setCookie = (name, value, maxAge = COOKIE_MAX_AGE_SECONDS) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax`;
};

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
};

const readParams = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  const params = new URLSearchParams(window.location.search);
  const data = {};
  ATTR_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      data[key] = value;
    }
  });
  return data;
};

const buildTouchPayload = (params) => ({
  ...params,
  page_url: window.location.href,
  page_path: window.location.pathname,
  referrer: document.referrer || '',
  timestamp: new Date().toISOString(),
});

const persistAttribution = () => {
  if (typeof window === 'undefined' || attributionInitialized) {
    return;
  }
  attributionInitialized = true;

  try {
    const params = readParams();
    const touchPayload = buildTouchPayload(params);
    const firstTouchStored = window.localStorage.getItem(TOUCH_STORAGE.first);
    const firstTouch = firstTouchStored ? safeJsonParse(firstTouchStored, touchPayload) : touchPayload;
    const lastTouch = touchPayload;

    if (!firstTouchStored) {
      window.localStorage.setItem(TOUCH_STORAGE.first, JSON.stringify(firstTouch));
    }
    window.localStorage.setItem(TOUCH_STORAGE.last, JSON.stringify(lastTouch));

    Object.entries(firstTouch).forEach(([key, value]) => {
      setCookie(`ctm_attr_first_${key}`, String(value));
    });
    Object.entries(lastTouch).forEach(([key, value]) => {
      setCookie(`ctm_attr_last_${key}`, String(value));
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'traffic_context_ready',
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || '',
      source_data: { first_touch: firstTouch, last_touch: lastTouch },
      first_touch: firstTouch,
      last_touch: lastTouch,
    });
  } catch (_) {
    // Ignore storage errors.
  }
};

const loadGtm = () => {
  if (typeof window === 'undefined' || !GTM_CODE || gtmInitialized) {
    return;
  }
  gtmInitialized = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CODE}`;
  document.head.appendChild(script);
};

const runCtmWhenReady = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const tryRun = (attempt = 0) => {
    if (window.__ctm?.main?.runNow) {
      window.__ctm.main.runNow();
      return;
    }
    if (attempt < 12) {
      setTimeout(() => tryRun(attempt + 1), 250);
    }
  };
  tryRun();
};

const installDeferredGtmLoader = () => {
  if (typeof window === 'undefined' || !GTM_CODE) {
    return;
  }

  let armed = true;
  const passiveOptions = { passive: true };
  const trigger = () => {
    if (!armed) {
      return;
    }
    armed = false;
    loadGtm();
    window.removeEventListener('scroll', trigger, passiveOptions);
    window.removeEventListener('pointerdown', trigger, passiveOptions);
    window.removeEventListener('keydown', trigger);
  };

  window.addEventListener('scroll', trigger, passiveOptions);
  window.addEventListener('pointerdown', trigger, passiveOptions);
  window.addEventListener('keydown', trigger);

  const hasPaidParams = ['gclid', 'fbclid', 'msclkid', 'utm_source', 'utm_campaign'].some((key) =>
    new URLSearchParams(window.location.search).has(key)
  );

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => trigger(), { timeout: hasPaidParams ? 800 : 2000 });
  }
  setTimeout(trigger, hasPaidParams ? 1200 : 2800);
};

const installPhoneClickTracking = () => {
  if (typeof document === 'undefined') {
    return;
  }

  document.addEventListener('click', (event) => {
    const anchor = event.target?.closest?.('a[href^="tel:"]');
    if (!anchor) {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'phone_click',
      phone_href: anchor.getAttribute('href') || '',
    });
  });
};

if (typeof window !== 'undefined') {
  persistAttribution();
  installDeferredGtmLoader();
  installPhoneClickTracking();
  runCtmWhenReady();
}

export const onRouteUpdate = () => {
  persistAttribution();
  runCtmWhenReady();
};

